/**
 * RestoreConfirmDialog — the second step of a restore.
 *
 * Opened by ``SnapshotsTab`` when the user clicks "Make active" on any
 * snapshot row. Responsibilities:
 *
 *   1. Fetch the dry-run preview for the selected source (``getRestorePreview``).
 *   2. Render the diff counts + first few per-agent names so the user can
 *      eyeball what's about to change.
 *   3. Show the type-to-confirm input. Button stays disabled until the
 *      input matches the network name (pure check from ``restoreDialog.js``).
 *   4. On submit, call ``restoreNetwork`` and report success / error via
 *      ``onRestored``.
 *
 * All decision logic (button state, diff copy, confirm matching) lives in
 * ``src/utils/restoreDialog.js`` so this component stays a presenter.
 */
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  getRestorePreview,
  restoreNetwork,
  RestoreActiveReleaseGraphBlockError,
} from '@/services/networkRestoreApi';
import {
  canSubmitRestore,
  confirmMatchesNetworkName,
  describeDiff,
  diffBuckets,
  hasAnyChanges,
} from '@/utils/restoreDialog';
import {
  snapshotPrimaryLabel,
  snapshotSubLabel,
  SNAPSHOT_KIND_CHECKPOINT,
} from '@/utils/snapshotList';

export default function RestoreConfirmDialog({
  open,
  networkId,
  networkName,
  source,
  sourceEntry,
  onClose,
  onRestored,
}) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [typedConfirm, setTypedConfirm] = useState('');
  // PR 3: server returns 409 ACTIVE_RELEASE_GRAPH_CHANGE when the snapshot
  // would rewire the deployed Step Functions while customer pools are still
  // pinned to active releases. We capture the block payload and offer an
  // explicit override on a second submit instead of silently auto-retrying.
  const [graphBlock, setGraphBlock] = useState(null);
  // PR 4: "Restore config only (keep current graph)" — visible only when
  // the preview reports ``graphChanged`` so the user isn't presented with
  // a toggle that has no effect. Defaults to OFF: a full restore is the
  // default intent, and ``configOnly`` is an explicit opt-in to leave the
  // Step Functions definition untouched.
  const [configOnly, setConfigOnly] = useState(false);

  // Preview fetch — fires when the user opens the dialog against a new source.
  useEffect(() => {
    if (!open || !networkId || !source) return undefined;
    let cancelled = false;
    setPreview(null);
    setLoadError(null);
    setLoading(true);
    setTypedConfirm('');
    setGraphBlock(null);
    setConfigOnly(false);
    getRestorePreview(networkId, source)
      .then((p) => {
        if (cancelled) return;
        setPreview(p);
      })
      .catch((e) => {
        if (cancelled) return;
        setLoadError(e?.message || 'Failed to preview restore');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, networkId, source?.sourceKind, source?.sourceVersion, source?.sourceCheckpointAt]);

  const buckets = diffBuckets(preview);
  const hasChanges = hasAnyChanges(preview);
  const canSubmit = canSubmitRestore({
    preview,
    networkName,
    typedConfirm,
    loading,
    submitting,
  });
  const confirmOk = confirmMatchesNetworkName(typedConfirm, networkName);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await restoreNetwork(networkId, source, {
        confirm: typedConfirm.trim(),
        // Only forward ``configOnly`` when the toggle is ON AND the
        // preview actually reports graph drift. This keeps the server's
        // ``warnings`` clean — we never tell a user "graph skipped" when
        // there was no graph change to skip.
        configOnly: configOnly && Boolean(preview?.graphChanged),
      });
      onRestored?.(res);
    } catch (err) {
      if (err instanceof RestoreActiveReleaseGraphBlockError) {
        // Surface the gate inline with an explicit override button. We do
        // NOT auto-retry — the operator must click "Restore anyway" to
        // confirm they understand customer pools will see new behavior.
        setGraphBlock(err.block);
      } else {
        setSubmitError(err?.message || 'Restore failed');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleOverrideSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await restoreNetwork(networkId, source, {
        confirm: typedConfirm.trim(),
        allowReleaseGraphChange: true,
        configOnly: configOnly && Boolean(preview?.graphChanged),
      });
      onRestored?.(res);
    } catch (err) {
      setSubmitError(err?.message || 'Restore failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          aria-modal="true"
          role="dialog"
        >
          <motion.div
            className="w-full max-w-lg bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden"
            initial={{ scale: 0.98, y: 6 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.98, y: 6 }}
          >
            <header className="px-5 py-4 border-b border-gray-100 flex items-start justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold text-gray-900">
                  Restore network
                </h3>
                <p className="text-xs text-gray-600 mt-1">
                  <span className="font-medium text-gray-700">
                    {snapshotPrimaryLabel(sourceEntry)}
                  </span>
                  {sourceEntry && (
                    <>
                      {' '}
                      · {snapshotSubLabel(sourceEntry)}
                    </>
                  )}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-700 text-sm"
                aria-label="Close restore dialog"
              >
                ✕
              </button>
            </header>

            <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
              {loading && (
                <p className="text-sm text-gray-500">Previewing changes…</p>
              )}

              {loadError && (
                <p className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                  {loadError}
                </p>
              )}

              {!loading && !loadError && preview && (
                <>
                  <div
                    className={`rounded-lg border px-3 py-2 text-sm ${
                      hasChanges
                        ? 'bg-amber-50 border-amber-200 text-amber-900'
                        : 'bg-gray-50 border-gray-200 text-gray-700'
                    }`}
                  >
                    {describeDiff(preview)}
                  </div>

                  {hasChanges && (
                    <DiffSections preview={preview} buckets={buckets} />
                  )}

                  {preview?.graphChanged && (
                    <label className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900">
                      <input
                        type="checkbox"
                        checked={configOnly}
                        onChange={(e) => setConfigOnly(e.target.checked)}
                        className="mt-0.5 h-3.5 w-3.5 rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                        aria-label="Restore agent configuration only, keep the current graph"
                      />
                      <span className="flex-1">
                        <span className="font-semibold">
                          Restore config only (keep current graph)
                        </span>
                        <span className="block text-[11px] text-blue-800 leading-relaxed">
                          The snapshot&rsquo;s Step Functions graph differs from what&rsquo;s deployed. Check this to apply agent and configuration changes only — the live graph stays as-is.
                        </span>
                      </span>
                    </label>
                  )}

                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-700 flex flex-col gap-1">
                      Type <span className="font-mono text-[11px] bg-gray-100 rounded px-1 py-0.5">{networkName}</span> to confirm
                      <input
                        type="text"
                        value={typedConfirm}
                        onChange={(e) => {
                          setTypedConfirm(e.target.value);
                          setSubmitError(null);
                        }}
                        className={`mt-1 text-sm border rounded-lg px-3 py-2 ${
                          confirmOk
                            ? 'border-emerald-300 focus:ring-emerald-400 focus:border-emerald-400'
                            : 'border-gray-300 focus:ring-blue-400 focus:border-blue-400'
                        } outline-none focus:ring-2`}
                        autoComplete="off"
                        spellCheck={false}
                        placeholder={networkName}
                        aria-label="Type network name to confirm restore"
                      />
                    </label>
                  </div>

                  <p className="text-xs text-gray-500 leading-relaxed">
                    A pre-restore checkpoint will be saved automatically so you
                    can undo this within the next {preview?.source?.kind === SNAPSHOT_KIND_CHECKPOINT ? 10 : 10}{' '}
                    snapshots. Pools and customer memberships are never touched.
                  </p>
                </>
              )}

              {submitError && (
                <p className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                  {submitError}
                </p>
              )}

              {graphBlock && (
                <div className="text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2 space-y-1">
                  <p className="font-semibold text-red-800">
                    Active releases would change behavior
                  </p>
                  <p className="text-red-700">
                    {graphBlock.message
                      || 'This snapshot would rewire the deployed graph while customer pools are still pinned to active releases.'}
                  </p>
                  <p className="text-xs text-red-700">
                    Pinned active releases:{' '}
                    <span className="font-mono">{graphBlock.activeReleaseCount}</span>
                  </p>
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="text-sm px-3 py-1.5 rounded-lg border border-gray-300 text-gray-800 hover:bg-gray-50"
                  disabled={submitting}
                >
                  Cancel
                </button>
                {graphBlock ? (
                  <button
                    type="button"
                    onClick={handleOverrideSubmit}
                    disabled={submitting}
                    className="text-sm px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Restoring…' : 'Restore anyway (rewires releases)'}
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={!canSubmit}
                    className="text-sm px-3 py-1.5 rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Restoring…' : 'Restore now'}
                  </button>
                )}
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function DiffSections({ preview, buckets }) {
  const agents = preview?.agents || {};
  const fieldChanges = preview?.networkFieldChanges || {};
  return (
    <div className="space-y-2 text-xs">
      {buckets.add > 0 && (
        <AgentLine label="To add" tone="emerald" rows={agents.add} />
      )}
      {buckets.update > 0 && (
        <AgentLine label="To update" tone="blue" rows={agents.update} />
      )}
      {buckets.delete > 0 && (
        <AgentLine
          label="To delete"
          tone="red"
          rows={(agents.delete || []).map((id) => ({ agentId: id }))}
        />
      )}
      {buckets.networkFieldChanges > 0 && (
        <div className="border border-gray-100 rounded-lg px-3 py-2">
          <p className="text-[11px] uppercase tracking-wide font-semibold text-gray-600 mb-1">
            Network settings
          </p>
          <ul className="space-y-0.5">
            {Object.keys(fieldChanges).map((field) => (
              <li key={field} className="text-gray-700">
                <span className="font-mono">{field}</span>
                <span className="text-gray-500"> will change</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function AgentLine({ label, tone, rows }) {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const toneMap = {
    emerald: 'text-emerald-700 bg-emerald-50 border-emerald-100',
    blue: 'text-blue-700 bg-blue-50 border-blue-100',
    red: 'text-red-700 bg-red-50 border-red-100',
  };
  const cls = toneMap[tone] || 'text-gray-700 bg-gray-50 border-gray-100';
  return (
    <div className={`border rounded-lg px-3 py-2 ${cls}`}>
      <p className="text-[11px] uppercase tracking-wide font-semibold mb-1">
        {label}
      </p>
      <p className="font-mono text-[11px] break-all">
        {rows
          .slice(0, 6)
          .map((r) => r.name || r.agentId || '(unnamed)')
          .join(', ')}
        {rows.length > 6 ? `, +${rows.length - 6} more` : ''}
      </p>
    </div>
  );
}
